import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Loader2,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  AlertTriangle,
  MinusCircle,
  Lock,
} from "lucide-react";
import {
  getQuestions,
  analyzeAssessment,
  unlockReport,
} from "@/lib/assessmentApi";

const BOOK_URL = "https://calendar.app.google/MFyPpbRo7HG92Q1YA";
const NAVY = "#101828";
const BLUE = "#3457D5";

const OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "partial", label: "Partial" },
  { value: "no", label: "No" },
];

const font = { fontFamily: '"Inter", system-ui, -apple-system, sans-serif' };

const StatusBadge = ({ status }) => {
  const map = {
    Gap: { cls: "bg-red-50 text-red-700", Icon: AlertTriangle },
    Partial: { cls: "bg-amber-50 text-amber-700", Icon: MinusCircle },
    Solid: { cls: "bg-green-50 text-green-700", Icon: CheckCircle2 },
  };
  const { cls, Icon } = map[status] || map.Gap;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${cls}`}
    >
      <Icon className="h-3.5 w-3.5" /> {status}
    </span>
  );
};

const ScoreRing = ({ score, max }) => {
  const pct = Math.round((score / max) * 100);
  return (
    <div
      data-testid="score-ring"
      className="relative flex h-40 w-40 shrink-0 items-center justify-center rounded-full"
      style={{
        background: `conic-gradient(${BLUE} ${pct * 3.6}deg, #EAF1FE 0deg)`,
      }}
    >
      <div className="flex h-32 w-32 flex-col items-center justify-center rounded-full bg-white">
        <span className="text-4xl font-semibold" style={{ color: NAVY }}>
          {score}
        </span>
        <span className="text-sm text-slate-400">of {max}</span>
      </div>
    </div>
  );
};

export default function Assessment() {
  const [step, setStep] = useState("intro"); // intro | quiz | loading | result | report
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [idx, setIdx] = useState(0);
  const [result, setResult] = useState(null);
  const [report, setReport] = useState(null);
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    getQuestions()
      .then((d) => setQuestions(d.questions))
      .catch((e) => setError(e.message));
  }, []);

  const total = questions.length;
  const current = questions[idx];
  const answeredCount = Object.keys(answers).length;

  const choose = (value) => {
    const q = questions[idx];
    const next = { ...answers, [q.id]: value };
    setAnswers(next);
    if (idx < total - 1) {
      setIdx(idx + 1);
    } else {
      runAnalyze(next);
    }
  };

  const runAnalyze = async (allAnswers) => {
    setStep("loading");
    setError("");
    try {
      const data = await analyzeAssessment(allAnswers);
      setResult(data);
      setStep("result");
    } catch (e) {
      setError(e.message);
      setStep("quiz");
    }
  };

  const handleUnlock = async () => {
    setError("");
    setSubmitting(true);
    try {
      const data = await unlockReport(result.submissionId, email);
      setReport(data);
      setStep("report");
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const restart = () => {
    setAnswers({});
    setIdx(0);
    setResult(null);
    setReport(null);
    setEmail("");
    setError("");
    setStep("intro");
  };

  return (
    <div
      style={{ ...font, color: NAVY }}
      className="min-h-screen w-full overflow-x-hidden bg-white"
    >
      <header className="border-b border-slate-200">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-4">
          <Link
            data-testid="assessment-back-home"
            to="/"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 transition-colors hover:text-[#3457D5]"
          >
            <ArrowLeft className="h-4 w-4" /> Craig Maloney
          </Link>
          <span className="text-xs font-medium uppercase tracking-wide text-slate-400">
            Sellability Checklist
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-14">
        {/* INTRO */}
        {step === "intro" && (
          <div data-testid="assessment-intro">
            <span
              className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold"
              style={{ background: "#EAF1FE", color: BLUE }}
            >
              Free · ~3 minutes · no signup to see your score
            </span>
            <h1 className="mt-5 text-4xl font-semibold tracking-tight sm:text-5xl">
              Is your business sellable?
            </h1>
            <p className="mt-4 max-w-xl text-lg leading-relaxed text-slate-600">
              This isn&apos;t a SWOT analysis. It&apos;s the 15-point checklist a
              buyer runs through before they decide what your business is worth —
              or whether to walk. Answer honestly and see where you stand.
            </p>
            <button
              data-testid="assessment-start-btn"
              onClick={() => setStep("quiz")}
              disabled={!total}
              className="mt-8 inline-flex items-center justify-center gap-2 rounded-lg px-8 py-3.5 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-40"
              style={{ background: BLUE }}
            >
              {total ? "Start free assessment" : "Loading…"}
              {total ? <ArrowRight className="h-4 w-4" /> : null}
            </button>
            {error && (
              <p data-testid="assessment-error" className="mt-4 text-sm text-red-600">
                {error}
              </p>
            )}
          </div>
        )}

        {/* QUIZ */}
        {step === "quiz" && current && (
          <div data-testid="assessment-quiz">
            <div className="flex items-center justify-between text-sm text-slate-500">
              <span data-testid="quiz-progress">
                Question {idx + 1} of {total}
              </span>
              <span
                className="rounded-full px-2.5 py-0.5 text-xs font-medium"
                style={{ background: "#EAF1FE", color: BLUE }}
              >
                {current.category}
              </span>
            </div>
            <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${((idx) / total) * 100}%`, background: BLUE }}
              />
            </div>

            <h2 className="mt-8 text-2xl font-medium leading-snug">
              {current.text}
            </h2>

            <div className="mt-8 space-y-3">
              {OPTIONS.map((opt) => {
                const selected = answers[current.id] === opt.value;
                return (
                  <button
                    key={opt.value}
                    data-testid={`quiz-option-${opt.value}`}
                    onClick={() => choose(opt.value)}
                    className="flex w-full items-center justify-between rounded-xl border px-5 py-4 text-left text-base font-medium transition-colors hover:border-[#3457D5] hover:bg-[#F7FAFF]"
                    style={{
                      borderColor: selected ? BLUE : "#e2e8f0",
                      background: selected ? "#EAF1FE" : "white",
                    }}
                  >
                    {opt.label}
                    <ArrowRight className="h-4 w-4 text-slate-400" />
                  </button>
                );
              })}
            </div>

            {idx > 0 && (
              <button
                data-testid="quiz-back-btn"
                onClick={() => setIdx(idx - 1)}
                className="mt-8 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-[#3457D5]"
              >
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
            )}
          </div>
        )}

        {/* LOADING */}
        {step === "loading" && (
          <div
            data-testid="assessment-loading"
            className="flex flex-col items-center justify-center py-24"
          >
            <Loader2 className="h-8 w-8 animate-spin" style={{ color: BLUE }} />
            <p className="mt-4 text-slate-600">Scoring your business…</p>
          </div>
        )}

        {/* RESULT (free) */}
        {step === "result" && result && (
          <div data-testid="assessment-result" className="space-y-10">
            <div className="flex flex-col items-center gap-6 text-center sm:flex-row sm:items-center sm:text-left">
              <ScoreRing score={result.score} max={result.maxScore} />
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide" style={{ color: BLUE }}>
                  Your sellability score
                </p>
                <h1 className="mt-2 text-2xl font-semibold leading-snug sm:text-3xl">
                  {result.band}
                </h1>
                <p className="mt-3 text-lg font-medium text-slate-700">
                  {result.gutPunch}
                </p>
              </div>
            </div>

            <div>
              <h2 className="text-lg font-semibold">Your 3 weakest areas</h2>
              <div className="mt-4 space-y-4">
                {result.weaknesses.map((w, i) => (
                  <div
                    key={w.category}
                    data-testid={`weakness-${i}`}
                    className="rounded-xl border border-slate-200 p-5"
                    style={{ background: "#F7FAFF" }}
                  >
                    <p className="font-medium">{w.category}</p>
                    <p className="mt-1.5 text-sm leading-relaxed text-slate-600">
                      {w.cost}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Email gate */}
            <div
              data-testid="email-gate"
              className="rounded-2xl border p-7"
              style={{ borderColor: "#c7d7fb", background: "#EAF1FE" }}
            >
              <div className="flex items-center gap-2">
                <Lock className="h-5 w-5" style={{ color: BLUE }} />
                <h2 className="text-xl font-semibold">Unlock your full gap report</h2>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                See exactly what a buyer sees across all 15 items — and precisely
                what &quot;fixed&quot; looks like, in priority order by dollar
                impact. Enter your email to unlock it.
              </p>
              <div className="mt-5 flex flex-col gap-3 sm:flex-row">
                <input
                  data-testid="email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm outline-none focus:border-[#3457D5] focus:ring-2 focus:ring-blue-100"
                />
                <button
                  data-testid="unlock-btn"
                  onClick={handleUnlock}
                  disabled={submitting || !email.trim()}
                  className="inline-flex shrink-0 items-center justify-center rounded-lg px-6 py-3 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-40"
                  style={{ background: BLUE }}
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Unlock my report"
                  )}
                </button>
              </div>
              {error && (
                <p data-testid="unlock-error" className="mt-3 text-sm text-red-600">
                  {error}
                </p>
              )}
              <p className="mt-3 text-xs text-slate-500">
                Email is only used to send your report. No spam.
              </p>
            </div>

            <p className="text-xs text-slate-400">{result.disclaimer}</p>
          </div>
        )}

        {/* GATED REPORT */}
        {step === "report" && report && (
          <div data-testid="assessment-report" className="space-y-8">
            <div>
              <span
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold"
                style={{ background: "#EAF1FE", color: BLUE }}
              >
                <CheckCircle2 className="h-3.5 w-3.5" /> Full report unlocked
              </span>
              <h1 className="mt-4 text-3xl font-semibold tracking-tight">
                Your gap report
              </h1>
              <p className="mt-2 text-slate-600">
                Score {report.score}/{report.maxScore} — {report.band}. Fix these
                in order; the biggest dollar impact is at the top.
              </p>
            </div>

            <div className="space-y-4">
              {report.items.map((item, i) => (
                <div
                  key={item.id}
                  data-testid={`report-item-${i}`}
                  className="rounded-xl border border-slate-200 p-5"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
                        {item.category}
                      </p>
                      <p className="mt-1 font-medium">{item.question}</p>
                    </div>
                    <StatusBadge status={item.status} />
                  </div>
                  <div
                    className="mt-4 rounded-lg p-4 text-sm leading-relaxed text-slate-700"
                    style={{ background: "#F7FAFF" }}
                  >
                    <span className="font-semibold" style={{ color: BLUE }}>
                      What &quot;fixed&quot; looks like:{" "}
                    </span>
                    {item.fixedLooksLike}
                  </div>
                </div>
              ))}
            </div>

            {/* Upsell */}
            <div
              data-testid="report-upsell"
              className="rounded-2xl p-8 text-white"
              style={{ background: NAVY }}
            >
              <h2 className="text-2xl font-semibold">{report.upsell.heading}</h2>
              <p className="mt-3 max-w-xl text-sm leading-relaxed text-slate-300">
                {report.upsell.body}
              </p>
              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <Link
                  data-testid="upsell-deploy-btn"
                  to="/strategicroi"
                  className="inline-flex items-center justify-center rounded-lg px-6 py-3 text-sm font-medium text-white transition-opacity hover:opacity-90"
                  style={{ background: BLUE }}
                >
                  Deploy your agent team
                </Link>
                <a
                  data-testid="upsell-book-btn"
                  href={BOOK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center rounded-lg border border-white/30 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-white/10"
                >
                  Book a call
                </a>
              </div>
            </div>

            <button
              data-testid="report-restart-btn"
              onClick={restart}
              className="text-sm font-medium text-slate-500 hover:text-[#3457D5]"
            >
              Retake the assessment
            </button>

            <p className="text-xs text-slate-400">{report.disclaimer}</p>
          </div>
        )}
      </main>
    </div>
  );
}
